import { makeAutoObservable, runInAction } from 'mobx'
import { userManager } from '@/Core/Managers'
import { User } from '../Contracts'
import { LoadChatUsersTask } from '../Tasks/LoadChatUsersTask'

export class UserStore {
    users: User[] = []
    /**
     * Uuid'и підключених просто зараз — джерело правди: presence-канал
     * Centrifugo (useChatPresence). User.status — похідна від цього Set.
     */
    onlineUuids: Set<string> = new Set()
    isLoading = false
    error: string | null = null

    constructor() {
        makeAutoObservable(this)
    }

    /**
     * The current user comes from the app session (JWT), not from the chat
     * backend — a computed getter keeps it in sync with login/logout.
     */
    get currentUserId(): string {
        // Bracket access: the user object type declares an index signature
        return userManager.user?.['uuid'] ?? ''
    }

    get currentUser(): User | undefined {
        return this.getById(this.currentUserId)
    }

    getById(id: string): User | undefined {
        return this.users.find((u) => u.id === id)
    }

    getFullName(id: string): string {
        const user = this.getById(id)
        return user ? `${user.firstName} ${user.lastName}`.trim() : 'Невідомий'
    }

    /**
     * Merge users into the local registry. Called with the contacts list and
     * with users embedded in chat participants, so names resolve even for
     * people outside the contacts list.
     */
    upsertUsers(users: User[]) {
        for (const incoming of users) {
            // Мапери завжди ставлять 'offline' — реальний статус знає Set
            this.syncStatus(incoming)

            const index = this.users.findIndex((u) => u.id === incoming.id)
            if (index === -1) {
                this.users.push(incoming)
            } else {
                this.users[index] = incoming
            }
        }
    }

    /** Initial-знімок після підписки на presence-канал: повний перелік онлайнових */
    applyPresenceSnapshot(uuids: string[]) {
        this.onlineUuids = new Set(uuids)
        for (const user of this.users) {
            this.syncStatus(user)
        }
    }

    /** Realtime join: користувач підключився */
    applyPresenceJoin(uuid: string) {
        if (!uuid) return
        this.onlineUuids.add(uuid)

        const user = this.getById(uuid)
        if (user) {
            this.syncStatus(user)
        }
    }

    /** Realtime leave: відключився — «щойно в мережі» */
    applyPresenceLeave(uuid: string) {
        if (!uuid) return
        this.onlineUuids.delete(uuid)

        const user = this.getById(uuid)
        if (user) {
            this.syncStatus(user)
            user.lastSeen = Date.now()
        }
    }

    private syncStatus(user: User) {
        user.status = this.onlineUuids.has(user.id) ? 'online' : 'offline'
    }

    async loadUsers() {
        this.isLoading = true
        this.error = null
        try {
            const users = await LoadChatUsersTask()
            runInAction(() => {
                this.upsertUsers(users)
            })
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Failed to load users'
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }
}
